import { i18n } from '@vestico/i18n';

import { BraSupportAnswer, ComfortAnswer, FitAnswer, FitLikeAnswer, LengthAnswer, ReturningAnswer, SizeAnswer, SkinConcernAnswer } from './answer';

export interface Answer {
  questionUID: string;
  value: number | string | string[];
  language?: string; // Optionally set for free text review answers
}

export interface QuestionOption {
  value: number | string;
}

export interface CustomQuestionOption extends QuestionOption {
  name: string;
}

export enum QuestionType {
  multiOption = 0,
  text = 1,
  binary = 2,
  dynamicSelect = 3,
  rating = 4,
  multiSelect = 5,
}

export type Question = {
  uid: string;
  required: boolean;
  hidden?: boolean;
  type: QuestionType;
  standardizedCategory?: string;
};

export interface CustomQuestion extends Question {
  name: string;
}

export interface MultiOptionQuestion extends Question {
  options: QuestionOption[];
}

export interface BinaryQuestion extends Question {
  enabled: boolean;
}

export type RatingQuestion = Question;

export enum QuestionDataSourceType {
  productSize = 'productSize',
  productVariant = 'productVariant',
}

export interface DynamicSelectQuestion extends Question {
  contentCollection: QuestionDataSourceType;
}

export type TextQuestion = Question;

export function hasAnswerForQuestion(question: Question, answers: Answer[]): boolean {
  const index = indexOfAnswerForQuestion(question, answers);
  const answer = index !== null && index < answers.length ? answers[index] : null;
  switch (question.type) {
    case QuestionType.multiSelect:
    case QuestionType.multiOption: {
      const optionIndex = indexOfAnswerOptionForQuestion(question as MultiOptionQuestion, answers);
      return optionIndex !== null;
    }
    case QuestionType.text:
      // skipcq JS-0366
      return !!(answer && answer?.value.toString().length > 0);
    case QuestionType.binary:
    case QuestionType.dynamicSelect:
    case QuestionType.rating:
      // skipcq JS-0366
      return !!(answer && answer?.value !== undefined);
  }
  return false;
}

export function indexOfAnswerForQuestion(question: Question, answers: Answer[]): number | null {
  let existingAnswerIndex: number | null = null;

  // check if an answer already exists and replace it.
  // otherwise, just add it
  const questionUID = question.uid;
  answers.forEach((answer, index) => {
    if (answer.questionUID === questionUID) {
      existingAnswerIndex = index;
    }
  });

  return existingAnswerIndex;
}

export function indexOfAnswerOptionForQuestion(question: MultiOptionQuestion, answers: Answer[]): number | null {
  const answerIndex = indexOfAnswerForQuestion(question, answers);
  if (answerIndex === null) {
    return null;
  }

  let selectedOptionIndex: number | null = null;
  const { value } = answers[answerIndex];
  question.options.forEach((option, index) => {
    if (option.value === value) {
      selectedOptionIndex = index;
    }
  });
  return selectedOptionIndex;
}

export enum DefaultQuestionUID {
  usualSize = '9e06ee382c534b62abf1',
  size = '46659e859a2dfecc048f',
  sizeLike = 'IgBBOl0gmS87EEVfkXLJ',
  fit = 'kn4yTK46R4ITlCHu7HLD',
  length = 'gaqQSca4GKbU1nv4p719',
  braSize = 'wBtTfVnbDqFmpnUjpKYM',
  braBandFit = 'YasbsbCqq1vnViFb4t7I',
  braCupFit = '86STJrjbksDu87DtHaIg',
  braSupport = 'KXlyPZ7YrcUAk11RUhZp',
  pantySize = 'HPYfo5Lj6WvIGarjgZl3',
  comfort = '5QNnfuTjkjkCmz4iuLId',
  skinConcerns = 'CpekIDmlR77sM7UADgE1',
  rating = '5629a6f8c39d4188870a',
  fitLike = 'pGtoOCRPpGHSBASItIL2',
  return = 'xu0thLlEEYDO0IQeU0HT',
  reviewText = 'cfedeb9d8403477da27e',
}

export const SortedDefaultQuestionUIDs = Object.values(DefaultQuestionUID);
const DefaultQuestionUIDs: Set<string> = new Set(Object.values(DefaultQuestionUID));

export const questionsAreValid = (questions: Question[]) => {
  for (const question of questions) {
    if (!questionIsValid(question)) {
      return false;
    }
  }
  return true;
};

const questionIsValid = (question: Question) => {
  const basicInfoIsValid = typeof question.required === 'boolean' && typeof question.uid === 'string' && question.uid.length > 0;
  if (!basicInfoIsValid) {
    return false;
  }

  const customQuestion = isCustomQuestion(question);
  if (customQuestion) {
    const customQuestion = question as CustomQuestion;
    const customQuestionIsValid = typeof customQuestion.name === 'string' && customQuestion.name.length > 0;
    if (!customQuestionIsValid) {
      return false;
    }
  }

  const { type } = question;
  switch (type) {
    case QuestionType.multiOption:
      // skipcq JS-0072
      {
        const multiOptionQuestion = question as MultiOptionQuestion;
        const { options } = multiOptionQuestion;
        if (options.length === 0) {
          return false;
        }

        for (const option of options) {
          const optionIsValid = typeof option.value === 'number';
          if (!optionIsValid) {
            return false;
          }
          if (customQuestion) {
            if (typeof (option as CustomQuestionOption).name !== 'string') {
              return false;
            }
          }
        }
      }
      return true;
    case QuestionType.multiSelect:
    case QuestionType.text:
      return true;
    case QuestionType.binary: {
      const binaryQuestion = question as BinaryQuestion;
      return typeof binaryQuestion.enabled === 'boolean';
    }
    case QuestionType.dynamicSelect: {
      const selectQuestion = question as DynamicSelectQuestion;
      const { contentCollection } = selectQuestion;
      const objectTypeString = contentCollection.valueOf();
      return typeof objectTypeString.valueOf() === 'string' && objectTypeString.length > 0;
    }
    case QuestionType.rating:
      return true;
    default:
      throw new Error(`Unhandled question type ${type}`);
  }
};

export const isCustomQuestion = (question: Question) => {
  return !DefaultQuestionUIDs.has(question.uid);
};

export const localizedQuestionName = (question: Question) => {
  if (isCustomQuestion(question)) {
    return (question as CustomQuestion).name;
  }
  return localizedQuestionNameForDefaultQuestion(question.uid);
};

export const localizedQuestionNameForDefaultQuestion = (questionUID: string) => {
  switch (questionUID) {
    case DefaultQuestionUID.fit:
      return i18n.t('LOC_QUESTION_NAME_FIT', { ns: 'model' });
    case DefaultQuestionUID.size:
      return i18n.t('LOC_QUESTION_NAME_SIZE_WORN', { ns: 'model' });
    case DefaultQuestionUID.sizeLike:
      return i18n.t('LOC_QUESTION_NAME_SIZE', { ns: 'model' });
    case DefaultQuestionUID.length:
      return i18n.t('LOC_QUESTION_NAME_LENGTH', { ns: 'model' });
    case DefaultQuestionUID.fitLike:
      return i18n.t('LOC_QUESTION_NAME_FIT_LIKE', { ns: 'model' });
    case DefaultQuestionUID.return:
      return i18n.t('LOC_QUESTION_NAME_RETURN', { ns: 'model' });
    case DefaultQuestionUID.usualSize:
      return i18n.t('LOC_QUESTION_NAME_USUAL_SIZE', { ns: 'model' });
    case DefaultQuestionUID.reviewText:
      return i18n.t('LOC_QUESTION_NAME_REVIEW_TEXT', { ns: 'model' });
    case DefaultQuestionUID.rating:
      return i18n.t('LOC_QUESTION_NAME_RATING', { ns: 'model' });
    case DefaultQuestionUID.braSize:
      return i18n.t('LOC_QUESTION_NAME_BRA_SIZE', { ns: 'model' });
    case DefaultQuestionUID.braBandFit:
      return i18n.t('LOC_QUESTION_NAME_BRA_BAND_FIT', { ns: 'model' });
    case DefaultQuestionUID.braCupFit:
      return i18n.t('LOC_QUESTION_NAME_BRA_CUP_FIT', { ns: 'model' });
    case DefaultQuestionUID.braSupport:
      return i18n.t('LOC_QUESTION_NAME_BRA_SUPPORT', { ns: 'model' });
    case DefaultQuestionUID.pantySize:
      return i18n.t('LOC_QUESTION_NAME_PANTY_SIZE', { ns: 'model' });
    case DefaultQuestionUID.comfort:
      return i18n.t('LOC_QUESTION_NAME_COMFORT', { ns: 'model' });
    case DefaultQuestionUID.skinConcerns:
      return i18n.t('LOC_QUESTION_NAME_SKIN_CONCERNS', { ns: 'model' });
  }
  throw new Error(`Unhandled default question ${questionUID}`);
};

const fitAnswerOptionString = (fit: FitAnswer): string | undefined => {
  if (fit === undefined) {
    return undefined;
  }
  switch (fit) {
    case FitAnswer.none:
      return undefined;
    case FitAnswer.veryTight:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_FIT_VERY_TIGHT', { ns: 'model' });
    case FitAnswer.normal:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_FIT_PERFECT', { ns: 'model' });
    case FitAnswer.veryLoose:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_FIT_VERY_LOOSE', { ns: 'model' });
    case FitAnswer.littleLoose:
    case FitAnswer.loose:
    case FitAnswer.tight:
    case FitAnswer.littleTight:
      return ' ';
  }
  throw new Error(`Unhandled ${fit}`);
};

const sizeAnswerOptionString = (size: SizeAnswer): string | undefined => {
  if (size === undefined) {
    return undefined;
  }
  switch (size) {
    case SizeAnswer.none:
      return undefined;
    case SizeAnswer.verySmall:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_SIZE_TOO_SMALL', { ns: 'model' });
    case SizeAnswer.normal:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_SIZE_PERFECT', { ns: 'model' });
    case SizeAnswer.veryLarge:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_SIZE_TOO_LARGE', { ns: 'model' });

    case SizeAnswer.small:
    case SizeAnswer.littleSmall:
    case SizeAnswer.large:
    case SizeAnswer.littleLarge:
      // These are all shown as empty
      return ' ';
  }
  throw new Error(`Unhandled ${size}`);
};

const lengthAnswerOptionString = (length: LengthAnswer): string | undefined => {
  if (length === undefined) {
    return undefined;
  }
  switch (length) {
    case LengthAnswer.none:
      return undefined;
    case LengthAnswer.veryShort:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_LENGTH_VERY_SHORT', { ns: 'model' });
    case LengthAnswer.normal:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_LENGTH_PERFECT', { ns: 'model' });
    case LengthAnswer.veryLong:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_LENGTH_VERY_LONG', { ns: 'model' });
    case LengthAnswer.littleLong:
    case LengthAnswer.long:
    case LengthAnswer.short:
    case LengthAnswer.littleShort:
      return ' ';
  }
  throw new Error(`Unhandled ${length}`);
};

export const fitLikeAnswerOptionString = (fitLike: FitLikeAnswer): string | undefined => {
  if (fitLike === undefined) {
    return undefined;
  }
  switch (fitLike) {
    case FitLikeAnswer.none:
      return undefined;
    case FitLikeAnswer.no:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_FIT_LIKE_NO', { ns: 'model' });
    case FitLikeAnswer.yes:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_FIT_LIKE_YES', { ns: 'model' });
    case FitLikeAnswer.unsure:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_FIT_LIKE_UNSURE', { ns: 'model' });
  }
  throw new Error(`Unhandled ${fitLike}`);
};

const returningAnswerOptionString = (returning: ReturningAnswer): string | undefined => {
  if (returning === undefined) {
    return undefined;
  }
  switch (returning) {
    case ReturningAnswer.none:
      return undefined;
    case ReturningAnswer.returning:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_RETURNING_RETURNING', { ns: 'model' });
    case ReturningAnswer.keeping:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_RETURNING_KEEPING', { ns: 'model' });
    case ReturningAnswer.unsure:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_RETURNING_UNSURE', { ns: 'model' });
  }
  throw new Error(`Unhandled ${returning}`);
};

const braSupportOptionString = (support: BraSupportAnswer): string | undefined => {
  if (support === undefined) {
    return undefined;
  }
  switch (support) {
    case BraSupportAnswer.none:
      return undefined;
    case BraSupportAnswer.veryLow:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_BRA_SUPPORT_VERY_LOW', { ns: 'model' });
    case BraSupportAnswer.medium:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_BRA_SUPPORT_MEDIUM', { ns: 'model' });
    case BraSupportAnswer.veryHigh:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_BRA_SUPPORT_VERY_HIGH', { ns: 'model' });
    case BraSupportAnswer.littleHigh:
    case BraSupportAnswer.high:
    case BraSupportAnswer.low:
    case BraSupportAnswer.littleLow:
      return ' ';
  }
  throw new Error(`Unhandled ${support}`);
};

const comfortOptionString = (support: ComfortAnswer): string | undefined => {
  if (support === undefined) {
    return undefined;
  }
  switch (support) {
    case ComfortAnswer.none:
      return undefined;
    case ComfortAnswer.veryLow:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_COMFORT_VERY_LOW', { ns: 'model' });
    case ComfortAnswer.medium:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_COMFORT_MEDIUM', { ns: 'model' });
    case ComfortAnswer.veryHigh:
      return i18n.t('LOC_FIT_REVIEW_ANSWER_OPTION_COMFORT_VERY_HIGH', { ns: 'model' });
    case ComfortAnswer.littleHigh:
    case ComfortAnswer.high:
    case ComfortAnswer.low:
    case ComfortAnswer.littleLow:
      return ' ';
  }
  throw new Error(`Unhandled ${support}`);
};

export const titleForSkinConcern = (concern: SkinConcernAnswer) => {
  switch (concern) {
    case SkinConcernAnswer.blemishesBreakouts:
      return i18n.t('LOC_SKIN_CONCERN_BLEMISHES_BREAKOUTS', { ns: 'model' });
    case SkinConcernAnswer.redness:
      return i18n.t('LOC_SKIN_CONCERN_REDNESS', { ns: 'model' });
    case SkinConcernAnswer.unevenTexture:
      return i18n.t('LOC_SKIN_CONCERN_UNEVEN_TEXTURE', { ns: 'model' });
    case SkinConcernAnswer.blackheads:
      return i18n.t('LOC_SKIN_CONCERN_BLACKHEADS', { ns: 'model' });
    case SkinConcernAnswer.fineLinesWrinkles:
      return i18n.t('LOC_SKIN_CONCERN_FINE_LINES_WRINKLES', { ns: 'model' });
    case SkinConcernAnswer.drynessDehydration:
      return i18n.t('LOC_SKIN_CONCERN_DRYNESS_DEHYDRATION', { ns: 'model' });
    case SkinConcernAnswer.hyperPigmentation:
      return i18n.t('LOC_SKIN_CONCERN_HYPERPIGMENTATION', { ns: 'model' });
    case SkinConcernAnswer.oiliness:
      return i18n.t('LOC_SKIN_CONCERN_OILINESS', { ns: 'model' });
    case SkinConcernAnswer.pores:
      return i18n.t('LOC_SKIN_CONCERN_PORES', { ns: 'model' });
    default:
      throw new Error(`Unhandled concern ${concern}`);
  }
};

export const localizedQuestionOptionName = (option: QuestionOption, question: Question) => {
  if (isCustomQuestion(question)) {
    return (option as CustomQuestionOption).name;
  }

  const { value } = option;

  switch (question.uid) {
    case DefaultQuestionUID.fit:
    case DefaultQuestionUID.braBandFit:
    case DefaultQuestionUID.braCupFit:
      return fitAnswerOptionString(value as FitAnswer);
    case DefaultQuestionUID.sizeLike:
    case DefaultQuestionUID.braSize:
    case DefaultQuestionUID.pantySize:
      return sizeAnswerOptionString(value as SizeAnswer);
    case DefaultQuestionUID.length:
      return lengthAnswerOptionString(value as LengthAnswer);
    case DefaultQuestionUID.fitLike:
      return fitLikeAnswerOptionString(value as FitLikeAnswer);
    case DefaultQuestionUID.return:
      return returningAnswerOptionString(value as ReturningAnswer);
    case DefaultQuestionUID.braSupport:
      return braSupportOptionString(value as BraSupportAnswer);
    case DefaultQuestionUID.comfort:
      return comfortOptionString(value as ComfortAnswer);
    case DefaultQuestionUID.skinConcerns:
      return titleForSkinConcern(value as SkinConcernAnswer);
    case DefaultQuestionUID.size:
    case DefaultQuestionUID.usualSize:
    case DefaultQuestionUID.reviewText:
    case DefaultQuestionUID.rating:
      throw new Error(`Question ${question.uid} should not have options`);
  }
  throw new Error(`Unhandled default question ${question.uid}`);
};
